import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

export default function TeacherActivity() {
  const { user } = useAuth();
  const [activities, setActivities] = useState([]);
  const [filters, setFilters] = useState({
    dateRange: 'today',
    student: 'all',
    assignment: 'all',
    activityType: 'all',
    search: ''
  });
  const [loading, setLoading] = useState(true);
  const [realTimeEnabled, setRealTimeEnabled] = useState(true);

  useEffect(() => {
    fetchActivities();
    
    let interval;
    if (realTimeEnabled) {
      interval = setInterval(fetchActivities, 3000); // Update every 3 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [filters, realTimeEnabled]);

  const fetchActivities = async () => {
    // Mock data - in production, fetch from Firebase with real-time updates
    setTimeout(() => {
      const mockActivities = [
        {
          id: 1,
          timestamp: new Date(Date.now() - 1 * 60 * 1000),
          studentId: 'student1',
          studentName: 'Mike Chen',
          assignmentId: 'assign1',
          assignmentTitle: 'Biology Test',
          activityType: 'violation',
          action: 'Alt-Tab detection',
          details: 'Switched to Google Chrome browser',
          severity: 'high',
          icon: '🚨',
          color: 'text-red-600'
        },
        {
          id: 2,
          timestamp: new Date(Date.now() - 3 * 60 * 1000),
          studentId: 'student2',
          studentName: 'Sarah Johnson',
          assignmentId: 'assign1',
          assignmentTitle: 'Biology Test',
          activityType: 'completion',
          action: 'submitted assignment',
          details: 'Final score: 85/100 (85%)',
          severity: 'info',
          icon: '✅',
          color: 'text-green-600'
        },
        {
          id: 3,
          timestamp: new Date(Date.now() - 5 * 60 * 1000),
          studentId: 'student3',
          studentName: 'Alex Rodriguez',
          assignmentId: 'assign2',
          assignmentTitle: 'Math Quiz',
          activityType: 'help_request',
          action: 'requested help',
          details: 'Question 8: Need clarification on formula',
          severity: 'medium',
          icon: '🆘',
          color: 'text-orange-600'
        },
        {
          id: 4,
          timestamp: new Date(Date.now() - 8 * 60 * 1000),
          studentId: 'student4',
          studentName: 'Emma Wilson',
          assignmentId: 'assign1',
          assignmentTitle: 'Biology Test',
          activityType: 'login',
          action: 'started assignment',
          details: 'Exam session initiated',
          severity: 'info',
          icon: '🔐',
          color: 'text-blue-600'
        },
        {
          id: 5,
          timestamp: new Date(Date.now() - 12 * 60 * 1000),
          studentId: 'student5',
          studentName: 'David Kim',
          assignmentId: 'assign3',
          assignmentTitle: 'Chemistry Lab',
          activityType: 'progress',
          action: 'reached 50% completion',
          details: 'Completed 10 out of 20 questions',
          severity: 'info',
          icon: '📊',
          color: 'text-blue-600'
        },
        {
          id: 6,
          timestamp: new Date(Date.now() - 15 * 60 * 1000),
          studentId: 'student1',
          studentName: 'Mike Chen',
          assignmentId: 'assign1',
          assignmentTitle: 'Biology Test',
          activityType: 'answer_change',
          action: 'modified answer',
          details: 'Question 5: Changed from B to C',
          severity: 'low',
          icon: '✏️',
          color: 'text-gray-600'
        },
        {
          id: 7,
          timestamp: new Date(Date.now() - 18 * 60 * 1000),
          studentId: 'student6',
          studentName: 'Lisa Park',
          assignmentId: 'assign2',
          assignmentTitle: 'Math Quiz',
          activityType: 'time_warning',
          action: '5 minutes remaining',
          details: 'Automatic time warning sent',
          severity: 'medium',
          icon: '⏰',
          color: 'text-orange-600'
        },
        {
          id: 8,
          timestamp: new Date(Date.now() - 22 * 60 * 1000),
          studentId: 'student7',
          studentName: 'James Wilson',
          assignmentId: 'assign4',
          assignmentTitle: 'Physics Exam',
          activityType: 'screen_lock',
          action: 'screen automatically locked',
          details: 'Multiple violation threshold exceeded',
          severity: 'high',
          icon: '🔒',
          color: 'text-red-600'
        }
      ];
      
      setActivities(mockActivities);
      setLoading(false);
    }, 300);
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - timestamp) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'just now';
    if (diffInMinutes === 1) return '1 min ago';
    if (diffInMinutes < 60) return `${diffInMinutes} min ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours === 1) return '1 hour ago';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    
    return timestamp.toLocaleDateString();
  };

  const getActivityTypeColor = (type) => {
    switch (type) {
      case 'violation': return 'bg-red-100 text-red-800';
      case 'completion': return 'bg-green-100 text-green-800';
      case 'help_request': return 'bg-orange-100 text-orange-800';
      case 'login': return 'bg-blue-100 text-blue-800';
      case 'progress': return 'bg-purple-100 text-purple-800';
      case 'answer_change': return 'bg-gray-100 text-gray-800';
      case 'time_warning': return 'bg-yellow-100 text-yellow-800';
      case 'screen_lock': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const exportActivities = () => {
    const csvContent = [
      ['Timestamp', 'Student', 'Assignment', 'Activity Type', 'Action', 'Details', 'Severity'],
      ...filteredActivities.map(activity => [
        activity.timestamp.toISOString(),
        activity.studentName,
        activity.assignmentTitle,
        activity.activityType,
        activity.action,
        activity.details,
        activity.severity
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity_feed_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredActivities = activities.filter(activity => {
    if (filters.student !== 'all' && activity.studentId !== filters.student) return false;
    if (filters.assignment !== 'all' && activity.assignmentId !== filters.assignment) return false;
    if (filters.activityType !== 'all' && activity.activityType !== filters.activityType) return false;
    if (filters.search && !activity.studentName.toLowerCase().includes(filters.search.toLowerCase()) &&
        !activity.action.toLowerCase().includes(filters.search.toLowerCase()) &&
        !activity.details.toLowerCase().includes(filters.search.toLowerCase())) return false;
    
    // Date range filter
    const activityDate = new Date(activity.timestamp);
    const now = new Date();
    
    switch (filters.dateRange) {
      case 'today':
        if (activityDate.toDateString() !== now.toDateString()) return false;
        break;
      case 'week':
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        if (activityDate < weekAgo) return false;
        break;
      case 'month':
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        if (activityDate < monthAgo) return false;
        break;
    }
    
    return true;
  });

  const stats = {
    total: filteredActivities.length,
    violations: filteredActivities.filter(a => a.activityType === 'violation').length,
    completions: filteredActivities.filter(a => a.activityType === 'completion').length,
    helpRequests: filteredActivities.filter(a => a.activityType === 'help_request').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <div className="text-xl font-semibold text-gray-700">Loading activity feed...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
                <span className="mr-3">📍</span>
                Activity Feed
                {realTimeEnabled && (
                  <div className="ml-3 w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                )}
              </h1>
              <p className="text-gray-600 mt-1">Complete log of all student activities and system events</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setRealTimeEnabled(!realTimeEnabled)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  realTimeEnabled 
                    ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
              >
                {realTimeEnabled ? '⏸️ Pause' : '▶️ Resume'} Real-time
              </button>
              <Link
                to="/dashboard"
                className="px-4 py-2 text-blue-600 hover:text-blue-800 font-medium"
              >
                ← Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6 border">
            <div className="flex items-center">
              <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.total}</div>
              <div className="ml-2 text-gray-500">📊</div>
            </div>
            <div className="text-sm text-gray-600">Total Activities</div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border">
            <div className="flex items-center">
              <div className="text-2xl sm:text-3xl font-bold text-red-600">{stats.violations}</div>
              <div className="ml-2 text-red-500">🚨</div>
            </div>
            <div className="text-sm text-gray-600">Violations</div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border">
            <div className="flex items-center">
              <div className="text-2xl sm:text-3xl font-bold text-green-600">{stats.completions}</div>
              <div className="ml-2 text-green-500">✅</div>
            </div>
            <div className="text-sm text-gray-600">Completions</div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border">
            <div className="flex items-center">
              <div className="text-2xl sm:text-3xl font-bold text-orange-600">{stats.helpRequests}</div>
              <div className="ml-2 text-orange-500">🆘</div>
            </div>
            <div className="text-sm text-gray-600">Help Requests</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 border mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Filters & Search</h2>
            <button
              onClick={exportActivities}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
            >
              📊 Export CSV
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.dateRange}
                onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
              >
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="all">All Time</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Activity Type</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.activityType}
                onChange={(e) => setFilters(prev => ({ ...prev, activityType: e.target.value }))}
              >
                <option value="all">All Types</option>
                <option value="violation">Violations</option>
                <option value="completion">Completions</option>
                <option value="help_request">Help Requests</option>
                <option value="login">Logins</option>
                <option value="progress">Progress</option>
                <option value="answer_change">Answer Changes</option>
                <option value="time_warning">Time Warnings</option>
                <option value="screen_lock">Screen Locks</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assignment</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.assignment}
                onChange={(e) => setFilters(prev => ({ ...prev, assignment: e.target.value }))}
              >
                <option value="all">All Assignments</option>
                <option value="assign1">Biology Test</option>
                <option value="assign2">Math Quiz</option>
                <option value="assign3">Chemistry Lab</option>
                <option value="assign4">Physics Exam</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Student</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.student}
                onChange={(e) => setFilters(prev => ({ ...prev, student: e.target.value }))}
              >
                <option value="all">All Students</option>
                <option value="student1">Mike Chen</option>
                <option value="student2">Sarah Johnson</option>
                <option value="student3">Alex Rodriguez</option>
                <option value="student4">Emma Wilson</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => setFilters({ dateRange: 'today', student: 'all', assignment: 'all', activityType: 'all', search: '' })}
                className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Clear All
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Search by student name, action, or details..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            />
          </div>
        </div>

        {/* Activity Feed */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold text-gray-900">
              Activity Log ({filteredActivities.length} activities)
            </h2>
          </div>

          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {filteredActivities.map((activity) => (
              <div key={activity.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start space-x-4">
                  <div className={`text-xl ${activity.color} mt-1`}>
                    {activity.icon}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-1">
                      <h3 className="text-sm font-medium text-gray-900">
                        {activity.studentName}
                      </h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActivityTypeColor(activity.activityType)}`}>
                        {activity.activityType.replace('_', ' ')}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatTimeAgo(activity.timestamp)}
                      </span>
                    </div>
                    
                    <div className="text-sm text-gray-600 space-y-1">
                      <div><strong>Assignment:</strong> {activity.assignmentTitle}</div>
                      <div><strong>Action:</strong> {activity.action}</div>
                      {activity.details && (
                        <div className="text-gray-500 italic">{activity.details}</div>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-xs text-gray-400">
                    {activity.timestamp.toLocaleTimeString()}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredActivities.length === 0 && (
            <div className="p-12 text-center">
              <div className="text-4xl mb-4">📭</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No activities found</h3>
              <p className="text-gray-600">No activities match your current filters.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}